import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsArticles } from "@/mocks/news";
import { videos } from "@/mocks/videos";
import { programs } from "@/mocks/programs";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function SportsPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = "Sports | L+TV TÉLÉVISION";
  }, []);

  const sportsNews = useTranslatedList(newsArticles.filter((n) => n.category === "Sports"), ["headline"]);
  const sportsVideos = useTranslatedList(videos.filter((v) => v.category === "Sports"), ["title"]);
  const sportsPrograms = useTranslatedList(programs.filter((p) => p.category === "Sports"), ["name", "description", "category"]);

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <h1 className="text-2xl md:text-3xl font-bold text-white">{t("sports.title")}</h1>
          <p className="text-white/60 text-sm mt-1">{t("sports.subtitle")}</p>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        {/* Sports Programs */}
        <h2 className="ltv-section-title">{t("sports.programs")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {sportsPrograms.map((p) => (
            <Link key={p.id} to={`/programs/${p.slug}`} className="ltv-card group">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 left-3 bg-accent-500 text-white text-xs font-semibold px-2 py-1 rounded">{p.category}</div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground-900 group-hover:text-accent-600 transition-colors">{p.name}</h3>
                <p className="text-sm text-foreground-600 mt-1 line-clamp-2">{p.description}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Sports News */}
        <h2 className="ltv-section-title">{t("sports.news")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {sportsNews.length > 0 ? sportsNews.map((a) => (
            <Link key={a.id} to={`/news/${a.slug}`} className="ltv-card group">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={a.image} alt={a.headline} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground-900 line-clamp-2 group-hover:text-accent-600 transition-colors">{a.headline}</h3>
                <p className="text-sm text-foreground-500 mt-2">{a.author}</p>
              </div>
            </Link>
          )) : (
            <p className="text-foreground-500">{t("sports.newsComingSoon")}</p>
          )}
        </div>

        {/* Sports Videos */}
        <h2 className="ltv-section-title">{t("sports.videos")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {sportsVideos.length > 0 ? sportsVideos.map((v) => (
            <Link key={v.id} to={`/videos/${v.slug}`} className="ltv-card group">
              <div className="relative aspect-video overflow-hidden">
                <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                    <i className="ri-play-fill text-accent-500 text-lg ml-0.5"></i>
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">{v.duration}</div>
              </div>
              <div className="p-3">
                <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-accent-600 transition-colors">{v.title}</h3>
              </div>
            </Link>
          )) : (
            <p className="text-foreground-500">{t("sports.videosComingSoon")}</p>
          )}
        </div>
      </div>
    </div>
  );
}